import React from 'react';
import { Stack } from 'react-bootstrap';
import {
  ObjectFieldValueFragment,
  ChangeRequestDetailsFieldsFragment,
  EmployeeDataChangeRequestStatus,
} from '../../../../../__generated__/graphql';
import { FormatFieldValue } from '../../components/FormatFieldValue';
import { useChangeRequestData } from './hooks';
import {
  SalaryChangeOverview,
  SalaryChangeData,
} from '../../ChangeRequestForm/components/salaryChange';
import {
  PositionChangeOverview,
  PositionChangeData,
} from '../../ChangeRequestForm/components/positionChange';

type EmployeeFieldValue = string | number | boolean | null;
type EmployeeFieldChanges = Record<
  string,
  EmployeeFieldValue | SalaryChangeData | PositionChangeData
>;

type ChangeRequestDetailsProps = {
  changeRequest: {
    id: string;
    employeeId: string;
  };
};

const getChangeRequestNewValues = (
  request: ChangeRequestDetailsFieldsFragment,
): EmployeeFieldChanges => {
  return request.flair__Changes__c ? JSON.parse(request.flair__Changes__c) : {};
};

const getChangeRequestOldValues = (
  request: ChangeRequestDetailsFieldsFragment,
  recordFields: ReadonlyArray<ObjectFieldValueFragment>,
): EmployeeFieldChanges => {
  if (request.flair__Status__c === EmployeeDataChangeRequestStatus.Pending) {
    const oldValues: EmployeeFieldChanges = {};
    recordFields?.forEach((field) => {
      oldValues[field.fieldInfo.name] = field.value;
    });
    return oldValues;
  } else {
    return request.flair__Old_Values__c
      ? JSON.parse(request.flair__Old_Values__c)
      : {};
  }
};

export const ChangeRequestDetails: React.FC<ChangeRequestDetailsProps> = ({
  changeRequest,
}) => {
  const { request, recordFields, isLoading } = useChangeRequestData(
    changeRequest.id,
    changeRequest.employeeId,
    'flair__Employee__c',
    true,
  );

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center p-4">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="p-4 text-center text-muted">
        Unable to load change request details.
      </div>
    );
  }

  const changesNewValues = getChangeRequestNewValues(
    request,
  ) as EmployeeFieldChanges;
  const changesOldValues = getChangeRequestOldValues(request, recordFields);
  const salaryData = changesNewValues.salaryChange as SalaryChangeData;
  const positionData = changesNewValues.positionChange as PositionChangeData;
  const employeeId = request?.flair__Employee__c || '';

  return (
    <div className="fields-container">
      <Stack gap={3}>
        {positionData && (
          <PositionChangeOverview positionChange={positionData} />
        )}
        {salaryData && employeeId && (
          <SalaryChangeOverview
            salaryChange={salaryData}
            employeeId={employeeId}
          />
        )}

        {recordFields
          .filter((field) => field.fieldInfo.name in changesNewValues)
          .map((field) => {
            const newValue = changesNewValues[field.fieldInfo.name];
            const oldValue = changesOldValues[field.fieldInfo.name];

            return (
              <div
                key={field.fieldInfo.name}
                className="bg-light rounded p-4 mb-2">
                <div className="fw-bold mb-2 ">{field.fieldInfo.label}</div>
                <div className="text-break">
                  <FormatFieldValue
                    value={newValue?.toString() ?? null}
                    type={field.fieldInfo.type}
                  />
                </div>
                <div className="text-muted text-decoration-line-through text-break">
                  <FormatFieldValue
                    value={oldValue?.toString() ?? null}
                    type={field.fieldInfo.type}
                  />
                </div>
              </div>
            );
          })}
      </Stack>
    </div>
  );
};
